<!-- =============================================================================
  Initials · The PlateX Initials Reference — Phase 3 structure.
  Copy verbatim from the approved v5 prototype where static; every figure on
  this page is live plato-search data, feed-derived, or a verified sale
  record (no illustrative values). Demand ranking omitted per §8.2.
  Status rule: price >= 0 = Available now; price < 0 = Not currently
  advertised; Previously sold only from a qualifying verified sale.
============================================================================== -->

<!-- chapter navigation -->
<nav class="chapnav" aria-label="Chapters"><div class="wrap">
  <a *ngFor="let c of chapters; trackBy: trackByChapter"
     [class.hot]="c.hot"
     href="#{{c.id}}"
     (click)="jumpTo(c.id, $event)">{{c.label}}</a>
</div></nav>

<!-- SPLIT HERO (fixed decisions §6: blue Lamborghini, square edges, no
     overlay, no caption; compact initials input, not an oversized plate) -->
<section class="hero">
  <div class="wrap">
    <div class="hero-copy">
      <span class="eyebrow">The PlateX Initials Reference</span>
      <h1>Your initials, priced across the market.</h1>
      <p class="lede">Enter up to three letters. We show your initials on every format of UK registration, from a few hundred pounds to six figures.</p>
      <div class="mono-line">
        <div class="mono-plate" role="group" aria-label="Your initials">
          <input #mc1 class="mc" type="text" maxlength="1" [value]="cells[0]"
                 autocomplete="off" autocapitalize="characters" spellcheck="false"
                 aria-label="First initial"
                 (input)="onCellInput(0, mc1, mc2)"
                 (keydown)="onCellKeydown(0, $event, mc1)"
                 (paste)="onCellPaste($event, [mc1, mc2, mc3])" />
          <input #mc2 class="mc" type="text" maxlength="1" [value]="cells[1]"
                 autocomplete="off" autocapitalize="characters" spellcheck="false"
                 aria-label="Second initial"
                 (input)="onCellInput(1, mc2, mc3)"
                 (keydown)="onCellKeydown(1, $event, mc2, mc1)"
                 (paste)="onCellPaste($event, [mc1, mc2, mc3])" />
          <input #mc3 class="mc" type="text" maxlength="1" [value]="cells[2]" placeholder="·"
                 autocomplete="off" autocapitalize="characters" spellcheck="false"
                 aria-label="Third initial"
                 (input)="onCellInput(2, mc3)"
                 (keydown)="onCellKeydown(2, $event, mc3, mc2)"
                 (paste)="onCellPaste($event, [mc1, mc2, mc3])" />
        </div>
        <button class="explore" type="button" (click)="apply()">Explore {{typedInitials}} <span aria-hidden="true">→</span></button>
      </div>
      <p class="mono-hint">Type your initials. Paste or press Enter also work.</p>
    </div>
    <figure class="hero-fig">
      <img class="hero-img" src="assets/initials/initials-hero-lamborghini.jpg"
           alt="A blue Lamborghini Murcielago wearing the registration CL 5" />
    </figure>
  </div>
</section>

<!-- I · YOUR INITIALS (live market profile; every tile source-tagged) -->
<section class="section" id="profile"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">I</span>Your initials</span>
    <h2>{{initials}} on the market, at a glance.</h2>
    <p>Defined facts about your initials the moment you search, each labelled by source.</p>
  </div>

  <!-- loading -->
  <div class="feed-note" *ngIf="!anyLoaded && anySearching" role="status">
    Fetching live {{initials}} market data…
  </div>
  <!-- total failure -->
  <div class="feed-note error" *ngIf="allFailed">
    Live market data is unavailable right now.
    <button class="retry" type="button" (click)="retryAll()">Retry</button>
  </div>

  <ng-container *ngIf="anyLoaded">
    <div class="evidence" *ngIf="lowest || highest || registerRange > 0">
      <div class="ev" *ngIf="lowest">
        <div class="v">{{money(lowest.pricePence)}}</div>
        <div class="k">Lowest {{initials}} registration advertised today ({{plateText(lowest.registration)}})</div>
        <div class="src real">Live · PlateX</div>
      </div>
      <div class="ev" *ngIf="highest">
        <div class="v">{{money(highest.pricePence)}}</div>
        <div class="k">Highest {{initials}} registration advertised today ({{plateText(highest.registration)}})</div>
        <div class="src real">Live · PlateX</div>
      </div>
      <div class="ev" *ngIf="registerRange > 0">
        <div class="v">{{registerRange | number}}</div>
        <div class="k">Dateless range, {{initials}} 1 to {{initials}} {{registerRange | number}}, advertised or not</div>
        <div class="src">Register</div>
      </div>
    </div>
    <div class="feed-note" *ngIf="!lowest && !highest">
      No {{initials}} registrations are advertised on PlateX today.
    </div>
    <p class="ev-note">The lowest and highest prices are live PlateX data, refreshed on search. The register range is a reference constant derived from the register itself, not a count of what is for sale. Regtransfers listings are excluded from public results.</p>
  </ng-container>
</div></section>

<!-- II · BY BUDGET (cheapest advertised per band, live feed) -->
<section class="section tint" id="budget"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">II</span>By budget</span>
    <h2>Your initials at every budget.</h2>
    <p>Pick a price range and see the lowest priced way to wear {{initials}} within it. Format is shown, but budget comes first.</p>
  </div>

  <div class="feed-note" *ngIf="!anyLoaded && anySearching" role="status">Fetching live budget matches…</div>
  <div class="feed-note error" *ngIf="allFailed">
    Live budget matches are unavailable right now.
    <button class="retry" type="button" (click)="retryAll()">Retry</button>
  </div>

  <div class="bands" *ngIf="anyLoaded">
    <div class="band" *ngFor="let r of bands; trackBy: trackByBand">
      <div class="bt">{{r.band.label}}<b>Lowest priced</b></div>
      <ng-container *ngIf="r.pick; else bandEmpty">
        <span class="plate md"><span class="ch">{{plateText(r.pick)}}</span></span>
        <div class="fmt"><b>{{r.pickFormat}}</b><ng-container *ngIf="seller(r.pick)"><br>{{seller(r.pick)}}</ng-container></div>
        <div class="price">{{price(r.pick)}}<small>{{r.count | number}} advertised in this range</small></div>
        <a class="view" [routerLink]="plateLink(r.pick.registration)">View</a>
      </ng-container>
      <ng-template #bandEmpty>
        <div class="band-empty">No advertised {{initials}} registrations in this range today.</div>
      </ng-template>
    </div>
  </div>
</div></section>

<!-- III · BY FORMAT (live explorer over the five buckets) -->
<section class="section" id="explore"><div class="wrap">
  <div class="sec-head"><span class="eyebrow"><span class="ix">III</span>By format</span>
    <h2>Explore your initials by style.</h2>
    <p>Five ways to wear {{initials}}. Dateless letter-first and number-first are two arrangements of the same collectable mark.</p>
  </div>

  <div class="pillrow" role="tablist" aria-label="Format">
    <button class="fpill" type="button" role="tab"
            *ngFor="let b of buckets; trackBy: trackByBucket"
            [class.sel]="b.key === selectedKey"
            [attr.aria-selected]="b.key === selectedKey"
            (click)="selectFormat(b.key)">
      <span class="fl">{{b.label}}</span>
      <span class="fc" *ngIf="b.loaded && !b.failed">{{b.rows.length | number}} advertised</span>
    </button>
  </div>
  <p class="fmt-note">{{selected.note}}</p>

  <div class="reg-controls" *ngIf="selected.loaded && !selected.failed">
    <div class="statusfilter" role="group" aria-label="Status filter">
      <button type="button" [class.on]="statusFilter === 'all'" (click)="setStatusFilter('all')">All</button>
      <button type="button" [class.on]="statusFilter === 'available'" (click)="setStatusFilter('available')">Available now</button>
      <button type="button" [class.on]="statusFilter === 'unadvertised'" (click)="setStatusFilter('unadvertised')">Not advertised</button>
    </div>
    <label class="sort-lbl">Sort
      <select class="sort" [value]="sort" (change)="setSort($any($event.target).value)">
        <option value="price-asc">Price, low to high</option>
        <option value="price-desc">Price, high to low</option>
        <option value="reg-az">Registration, A to Z</option>
      </select>
    </label>
  </div>
  <p class="reg-legend" *ngIf="selected.loaded && !selected.failed"><b>Favourite</b> saves an advertised plate. <b>Wanted</b> tells us you would buy a registration that is not currently advertised if it reaches the market. Prices and availability are live PlateX data; Regtransfers listings are excluded.</p>

  <!-- per-bucket states -->
  <div class="feed-note" *ngIf="selected.searching" role="status">Fetching live {{selected.label}} results…</div>
  <div class="feed-note error" *ngIf="selected.failed && !selected.searching">
    This format could not be loaded.
    <button class="retry" type="button" (click)="retryBucket(selected)">Retry</button>
  </div>
  <div class="feed-note" *ngIf="selected.loaded && !selected.failed && !rowsView.length">
    <ng-container *ngIf="statusFilter === 'available'">No {{initials}} registrations are advertised in this format today.</ng-container>
    <ng-container *ngIf="statusFilter !== 'available'">No records in this view.</ng-container>
  </div>

  <div class="rlist" *ngIf="selected.loaded && !selected.failed && rowsView.length">
    <div class="row" *ngFor="let r of rowsPage; trackBy: trackByRow">
      <span class="plate md"><span class="ch">{{plateText(r)}}</span></span>

      <span class="chip-st av" *ngIf="statusOf(r) === 'available'"><i></i>Available now</span>
      <span class="chip-st sold" *ngIf="statusOf(r) === 'unadvertised' && verifiedSale(r)"><i></i>Previously sold</span>
      <span class="chip-st unk" *ngIf="statusOf(r) === 'unadvertised' && !verifiedSale(r)"><i></i>Not currently advertised</span>

      <span class="facts2" *ngIf="statusOf(r) === 'available'">
        <b class="pf">{{price(r)}}</b>
        <span class="sf" *ngIf="seller(r)">{{seller(r)}} · advertised now</span>
        <span class="sf" *ngIf="!seller(r)">Advertised now</span>
      </span>
      <span class="facts2" *ngIf="statusOf(r) === 'unadvertised'">
        <ng-container *ngIf="verifiedSale(r)">
          <b class="pf">{{saleLine(r)}}</b>
          <span class="sf">Verified sale record</span>
        </ng-container>
      </span>

      <span class="rowacts" *ngIf="statusOf(r) === 'available'">
        <a class="rowbtn view" [routerLink]="plateLink(r.registration)">View registration</a>
        <button class="mini" type="button"
                [class.on]="isSaved(r)" [disabled]="isBusy(r)"
                [attr.aria-label]="isSaved(r) ? 'Remove from favourites' : 'Save to favourites'"
                [title]="isSaved(r) ? 'Remove from favourites' : 'Save to favourites'"
                (click)="toggleFavourite(r)">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 21s-7.5-4.6-10-9.3C.4 8.4 2 5 5.3 5c2 0 3.3 1.2 4.7 3 1.4-1.8 2.7-3 4.7-3C18 5 19.6 8.4 22 11.7 19.5 16.4 12 21 12 21z"/></svg>
        </button>
      </span>
      <span class="rowacts" *ngIf="statusOf(r) === 'unadvertised'">
        <button class="rowbtn want" type="button"
                [class.on]="isWanted(r)" [disabled]="isBusy(r)"
                (click)="toggleWanted(r)">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1z"/></svg>
          {{isWanted(r) ? 'Wanted ✓' : 'Mark as Wanted'}}
        </button>
      </span>
    </div>
    <nav class="pager" *ngIf="pageCount > 1" aria-label="Result pages">
      <ng-container *ngFor="let p of pagerItems">
        <span class="pg-gap" *ngIf="p < 0" aria-hidden="true">…</span>
        <button *ngIf="p > 0" type="button" class="pg"
                [class.on]="p === page" (click)="setPage(p)">{{p}}</button>
      </ng-container>
    </nav>
  </div>

  <!-- find a specific registration (canonical plate page) -->
  <div class="reg-find">
    <div>
      <h3>Looking for a specific {{initials}} registration?</h3>
      <p>Look up any {{initials}} registration and open its record page.</p>
      <div class="reg-ex"><span>Try</span>
        <button class="exchip" type="button" *ngFor="let ex of lookupExamples" (click)="runLookup(ex)">{{ex}}</button>
      </div>
    </div>
    <div class="reg-searchbox">
      <span class="pfx">{{initials}}</span>
      <input [(ngModel)]="lookupTerm" (keydown.enter)="runLookup()"
             placeholder="e.g. 1, 999, 9 {{initials}}" autocomplete="off" aria-label="Registration lookup" />
      <button class="reg-go" type="button" (click)="runLookup()">Find</button>
    </div>
  </div>
</div></section>

<!-- Wanted first-use explanation (once per session) -->
<div class="want-toast" *ngIf="wantedToast" role="status">Marked as Wanted. PlateX will alert you if this registration reaches the market.</div>

<!-- RELATED -->
<section class="rel" id="next"><div class="wrap">
  <span class="eyebrow">Where next</span>
  <h2>One set of initials, many ways to own them.</h2>
  <p>Whatever you enter, the same market and verified pricing follow you through every format.</p>
  <div class="rel-grid">
    <ng-container *ngFor="let c of collections; trackBy: trackByCard">
      <a *ngIf="c.link" class="rel-card" [routerLink]="c.link">
        <h4>{{c.title}}</h4><p>{{c.description}}</p>
      </a>
      <div *ngIf="!c.link" class="rel-card">
        <h4>{{c.title}}</h4><p>{{c.description}}</p>
      </div>
    </ng-container>
  </div>
</div></section>
